import React from "react";
import { Box, CircularProgress, Typography } from "@mui/joy";
import { Send, RotateCw, FileDown } from "lucide-react";
import { Button } from "../../shared";
import { SideModal } from "../";

interface IContractPreview {
    open: boolean;
    onClose: () => void;
    /** Object URL for backend-rendered PDF (revoked by parent on close). */
    pdfUrl: string | null;
    isLoadingPreview?: boolean;
    previewError?: string | null;
    onGenerate?: () => void;
    isGenerating?: boolean;
    onSend?: () => void;
    isSending?: boolean;
    isSendDisabled?: boolean;
    onDownload?: () => void;
    isDownloading?: boolean;
    isDownloadDisabled?: boolean;
    onSignNow?: () => void;
    isSigningNow?: boolean;
}

/**
 * Contract preview shell — shows the same PDF the backend uses for generate/sign.
 */
export default function ContractPreview({
    open,
    onClose,
    pdfUrl,
    isLoadingPreview,
    previewError,
    onGenerate,
    isGenerating,
    onSend,
    isSending,
    isSendDisabled,
    onDownload,
    isDownloading,
    isDownloadDisabled,
    onSignNow,
    isSigningNow,
}: IContractPreview) {
    return (
        <SideModal
            open={open}
            onClose={onClose}
            title="Contract Preview"
            description="Same PDF layout used when generating and sending for signature."
            showAdditionalHeadersIcons={false}
            portalBoxProps={{ width: "min(900px, 96vw)" }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "calc(100vh - 88px)",
                    minHeight: 0,
                    bgcolor: "background.level1",
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        p: 0,
                    }}
                >
                    {isLoadingPreview ? (
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1.5,
                            }}
                        >
                            <CircularProgress size="md" />
                            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                                Rendering contract PDF…
                            </Typography>
                        </Box>
                    ) : previewError ? (
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 2,
                            }}
                        >
                            <Typography level="body-sm" color="danger" textAlign="center">
                                {previewError}
                            </Typography>
                        </Box>
                    ) : pdfUrl ? (
                        <iframe
                            src={pdfUrl}
                            title="Contract PDF preview"
                            style={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                                display: "block",
                                flex: 1,
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                                No preview available.
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        p: 2,
                        display: "flex",
                        gap: 2,
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexShrink: 0,
                        bgcolor: "background.level1",
                        borderTop: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    {onDownload ? (
                        <Button
                            variant="outlined"
                            color="neutral"
                            startDecorator={<FileDown size={20} />}
                            loading={isDownloading}
                            disabled={isDownloadDisabled}
                            onClick={onDownload}
                            sx={{ px: 3 }}
                        >
                            Download PDF
                        </Button>
                    ) : (
                        <Box />
                    )}

                    <Box sx={{ display: "flex", gap: 2, ml: "auto", flexWrap: "wrap" }}>
                        {isSendDisabled ? (
                            <Button
                                variant="soft"
                                color="primary"
                                startDecorator={<RotateCw size={20} />}
                                loading={isGenerating}
                                onClick={() => onGenerate?.()}
                                sx={{ px: 4 }}
                            >
                                Regenerate Contract
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    color="neutral"
                                    startDecorator={<RotateCw size={20} />}
                                    loading={isGenerating}
                                    onClick={() => onGenerate?.()}
                                    sx={{ px: 4 }}
                                >
                                    Regenerate Contract
                                </Button>
                                <Button
                                    variant="soft"
                                    color="primary"
                                    startDecorator={<Send size={20} />}
                                    loading={isSending}
                                    onClick={onSend}
                                    sx={{ px: 4 }}
                                >
                                    Send for Signing
                                </Button>
                                <Button
                                    variant="solid"
                                    color="primary"
                                    loading={isSigningNow}
                                    onClick={onSignNow}
                                    sx={{ px: 4 }}
                                >
                                    Sign Now
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </SideModal>
    );
}
